import React from "react";
import AttachmentAssessment from "../../../Create_Assessment/Attachment/AttachmentAssessment";
import CrossAttachmentAssessment from "../../../Create_Assessment/Attachment/CrossAttachmentAssessment";
import ZoomableImage from "../../../../../Components/zoomablePic";
import { Box, Paper, Typography, Chip, Divider } from "@mui/material";
import { BookOpen, CheckCircle2, XCircle } from "lucide-react";

const ACCENT = "#f77f00";
const ACCENT_SOFT = "#fff4e5";
const BORDER = "#e6e9ef";
const TEXT_PRIMARY = "#1f2733";
const TEXT_SECONDARY = "#5b6472";
const BG_SOFT = "#f8f9fb";

const PublishedImgType = ({ questionNumber, question }) => {
  if (!question) return <></>;

  const metaCards = [
    { label: "CHAPTER", value: question?.chapter?.chapterName },
    { label: "SKILLS", value: question?.skill?.name },
    { label: "TYPE OF QUESTION", value: question?.type?.name },
    { label: "DIFFICULTY", value: question?.difficulty_level?.name },
  ];

  const se = question?.subjective_evaluation;
  const aiEnabled = se?.enabled === true;

  return (
    <Paper
      elevation={0}
      sx={{
        mb: 2,
        p: 2,
        borderRadius: "14px",
        border: `1px solid ${BORDER}`,
        background: "#fff",
        transition: "all 0.2s ease",
        "&:hover": {
          borderColor: ACCENT,
          boxShadow: "0 4px 16px rgba(247,127,0,0.08)",
        },
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: 1.5,
          mb: 1.5,
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-start",
            gap: 1.5,
            flex: 1,
            minWidth: 0,
          }}
        >
          <Box
            sx={{
              width: 34,
              height: 34,
              borderRadius: "10px",
              background: `linear-gradient(135deg, ${ACCENT} 0%, #d35400 100%)`,
              color: "#fff",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: "13px",
              fontWeight: 700,
              flexShrink: 0,
              boxShadow: "0 3px 8px rgba(247,127,0,0.3)",
            }}
          >
            Q{questionNumber}
          </Box>

          <Typography
            sx={{
              fontSize: "14.5px",
              fontWeight: 600,
              color: TEXT_PRIMARY,
              lineHeight: 1.5,
              wordBreak: "break-word",
            }}
          >
            {question?.title?.split("\n").map((line, i) => (
              <React.Fragment key={i}>
                {i !== 0 && <br />}
                <span>{line}</span>
              </React.Fragment>
            ))}
          </Typography>
        </Box>

        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-end",
            gap: 0.5,
            flexShrink: 0,
          }}
        >
          {question?.correct_score != null && (
            <Chip
              label={`${question.correct_score} Marks`}
              size="small"
              sx={{
                background: ACCENT_SOFT,
                color: ACCENT,
                fontWeight: 700,
                fontSize: "11.5px",
                border: `1px solid ${ACCENT}`,
              }}
            />
          )}
          {question?.is_mandatory && (
            <Chip
              label="* Mandatory"
              size="small"
              sx={{
                background: "#fff0f0",
                color: "#dc3545",
                fontWeight: 600,
                fontSize: "10.5px",
                border: "1px solid #f5c2c7",
              }}
            />
          )}
        </Box>
      </Box>

      {/* Attachments */}
      {question?.attachments?.length > 0 && (
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 1.5 }}>
          {question.attachments.map((attachment, index) => {
            const normalizedAttachment = attachment.startsWith(".")
              ? attachment.slice(1)
              : attachment;

            const ext = normalizedAttachment.split(".").pop().toLowerCase();
            const isImage = ["jpg", "jpeg", "png"].includes(ext);

            return (
              <Box key={index}>
                {isImage ? (
                  <ZoomableImage src={normalizedAttachment} />
                ) : (
                  <CrossAttachmentAssessment attachment={attachment} />
                )}
              </Box>
            );
          })}
        </Box>
      )}

      {/* Meta grid */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr 1fr", md: "repeat(4, 1fr)" },
          gap: 1,
          mb: 1.5,
        }}
      >
        {metaCards.map((m, i) => (
          <Box
            key={i}
            sx={{
              p: 1,
              px: 1.25,
              background: BG_SOFT,
              border: `1px solid ${BORDER}`,
              borderRadius: "10px",
            }}
          >
            <Typography
              sx={{
                fontSize: "10px",
                fontWeight: 700,
                color: TEXT_SECONDARY,
                letterSpacing: "0.4px",
                mb: 0.25,
              }}
            >
              {m.label}
            </Typography>
            <Typography
              sx={{
                fontSize: "12.5px",
                fontWeight: 600,
                color: TEXT_PRIMARY,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
              title={m.value}
            >
              {m.value || "—"}
            </Typography>
          </Box>
        ))}
      </Box>

      {/* Chips */}
      <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 1.5 }}>
        <Chip
          label={`Add for Homework: ${
            question?.add_to === "homework" ? "Yes" : "No"
          }`}
          size="small"
          sx={{
            background: "#fff",
            color: TEXT_PRIMARY,
            fontWeight: 600,
            fontSize: "11px",
            border: `1px solid ${BORDER}`,
          }}
        />
      </Box>

      <Divider sx={{ borderColor: BORDER, my: 1.5 }} />

      {/* Correct Answer / Reason */}
      {question?.correct_answers?.length > 0 && (
        <Paper
          elevation={0}
          sx={{
            p: 1.5,
            borderRadius: "10px",
            background: "#e8f7ee",
            border: "1px solid #b7e4c7",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.75,
              mb: 0.75,
            }}
          >
            <BookOpen size={14} color="#22c55e" />
            <Typography
              sx={{
                fontSize: "11px",
                fontWeight: 700,
                color: "#1a7f37",
                letterSpacing: "0.4px",
              }}
            >
              CORRECT ANSWER / REASON
            </Typography>
          </Box>

          {question.correct_answers.map((answer, index) => {
            const explanation = answer?.reason_for_answer?.explanation;
            const attachment = answer?.reason_for_answer?.attachment;

            return (
              <Box
                key={index}
                sx={{
                  mb: index < question.correct_answers.length - 1 ? 1.25 : 0,
                }}
              >
                {explanation && (
                  <Typography
                    sx={{
                      fontSize: "13px",
                      color: TEXT_PRIMARY,
                      lineHeight: 1.55,
                      mb: attachment ? 0.75 : 0,
                    }}
                  >
                    {explanation}
                  </Typography>
                )}

                {attachment && (
                  <Box sx={{ display: "inline-block", mt: 0.5 }}>
                    <CrossAttachmentAssessment attachment={attachment} />
                  </Box>
                )}
              </Box>
            );
          })}
        </Paper>
      )}

      {/* 🔹 AI Auto-Evaluation Setup */}
      {se && (
        <Paper
          elevation={0}
          sx={{
            mt: 1.5,
            p: 1.5,
            borderRadius: "10px",
            background: ACCENT_SOFT,
            border: `1px solid ${ACCENT}`,
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              mb: 1,
              gap: 1,
            }}
          >
            <Typography
              sx={{
                fontSize: "11px",
                fontWeight: 700,
                color: ACCENT,
                letterSpacing: "0.4px",
              }}
            >
              AI AUTO-EVALUATION SETUP
            </Typography>

            {aiEnabled ? (
              <Chip
                icon={<CheckCircle2 size={12} color="#fff" />}
                label="AI Evaluation: ON"
                size="small"
                sx={{
                  background: "#22c55e",
                  color: "#fff",
                  fontWeight: 700,
                  fontSize: "10.5px",
                  height: 22,
                  "& .MuiChip-icon": { ml: "6px", color: "#fff" },
                }}
              />
            ) : (
              <Chip
                icon={<XCircle size={12} color="#fff" />}
                label="AI Evaluation: OFF"
                size="small"
                sx={{
                  background: "#9ca3af",
                  color: "#fff",
                  fontWeight: 700,
                  fontSize: "10.5px",
                  height: 22,
                  "& .MuiChip-icon": { ml: "6px", color: "#fff" },
                }}
              />
            )}
          </Box>

          {se.expectedAnswer && (
            <Box sx={{ mb: 1 }}>
              <Typography
                sx={{
                  fontSize: "11px",
                  fontWeight: 600,
                  color: TEXT_SECONDARY,
                  mb: 0.25,
                }}
              >
                Expected Answer
              </Typography>
              <Typography
                sx={{
                  fontSize: "13px",
                  color: TEXT_PRIMARY,
                  lineHeight: 1.5,
                  opacity: aiEnabled ? 1 : 0.65,
                }}
              >
                {se.expectedAnswer}
              </Typography>
            </Box>
          )}

          {Array.isArray(se.rubric) && se.rubric.length > 0 && (
            <Box>
              <Typography
                sx={{
                  fontSize: "11px",
                  fontWeight: 600,
                  color: TEXT_SECONDARY,
                  mb: 0.5,
                }}
              >
                Marking Rubric
              </Typography>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 0.5,
                  opacity: aiEnabled ? 1 : 0.65,
                }}
              >
                {se.rubric.map((r, idx) => (
                  <Box
                    key={idx}
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      p: 0.75,
                      px: 1,
                      background: "#fff",
                      border: `1px solid ${BORDER}`,
                      borderRadius: "8px",
                      gap: 1,
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: "12.5px",
                        color: TEXT_PRIMARY,
                        flex: 1,
                      }}
                    >
                      {r.criterion || "—"}
                    </Typography>
                    <Chip
                      label={`${r.maxMarks || 0} Marks`}
                      size="small"
                      sx={{
                        background: ACCENT_SOFT,
                        color: ACCENT,
                        fontWeight: 700,
                        fontSize: "10.5px",
                        height: 22,
                      }}
                    />
                  </Box>
                ))}
              </Box>
            </Box>
          )}
        </Paper>
      )}
    </Paper>
  );
};

export default PublishedImgType;
